/**
 * The deterministic baseline generator.
 *
 * Rule-based, no model, always available. It exists for three reasons:
 *
 *  1. The suggestion loop can run end-to-end today, and be tested, without an
 *     API key or a network call.
 *  2. It is the floor a model-backed generator must beat. If a model cannot
 *     out-design these rules, it is not earning its latency.
 *  3. It is the fallback when generation fails or is unavailable. A user must
 *     never be left with no interface because a provider had an outage.
 */

import {
  SPEC_VERSION, VOCABULARY_VERSION, ROLE_KIND, DERIVATIVE_OF, enumPairKey, isProse,
  isRelationRole, isVisualKind,
  DENSITY_LEVEL, DENSITIES, TYPE_SYSTEMS, TYPE_SYSTEM_CHARACTER,
  type TBinding, type TLayoutSpec, type TNode,
  type Mutation, type Role, type RoleKind, type Density, type TypeSystem,
} from '@morph/spec';
import { resolveParams, type ResolvedParams, type ParamOverrides } from './params.js';
import type { GenerationRequest, Generator, ObjectShape, UseContext } from './types.js';
import { reviseAccepted } from './revise.js';
import { buildAppTemplate } from './app-template.js';

/**
 * Which mutation writes a role of this KIND — or nothing, when the closed
 * vocabulary has no legal write for it.
 *
 * Decision 0007 makes a binding directional, and until this table existed every
 * binding the baseline emitted said `read`. That is not a neutral default: it
 * means `mintGrants` returns an empty set, the renderer emits no editable
 * element, and the deployed lens is a picture of a database. A generator that
 * can only produce read-only layouts cannot produce Notion, which is the
 * specific thing 0007 was written about.
 *
 * Three kinds are deliberately absent, each because the write would be refused
 * downstream and an affordance that always refuses is worse than none:
 *
 *  - `media` — a media value is a blob reference; replacing one is ingestion's
 *    business, not a text field's.
 *  - `ref` — re-pointing a relation is `node-slot.editable`'s business, and a
 *    ref value is an object identity rather than something a user can type.
 *
 * `enum` used to be the third, on the grounds that *"`set-enum` is performed
 * with a CHOOSER, and no renderer emits one yet"*. The renderer emits one now,
 * so the entry moved to `chooseableEnums` below — which is a different KIND of
 * rule, and that is the reason it is not simply a fourth line in this table.
 */
const MUTATION_FOR_KIND: Partial<Record<RoleKind, Mutation>> = {
  text: 'set-value',
  number: 'set-value',
  score: 'set-value',
  date: 'set-date',
};

const EMPTY: ReadonlySet<Role> = new Set();

/**
 * Which enum roles may be CHOSEN from.
 *
 * Not every enum can be. A chooser's options are the values the collection
 * already holds, and that is only an honest offer while the set is small
 * enough to be a set: a role with fifty distinct values is not a chooser, it is
 * a text field wearing one, and a dropdown of fifty is worse than no dropdown.
 *
 * The CEILING is `groupableCardinalityMax`, reused rather than reinvented,
 * and reused because it is the SAME question — *"is this few enough values to
 * be treated as a closed set on screen"* — already answered once for grouping.
 * A second number for the same judgement would be a second thing to keep in
 * step, and this one is an invariant with a stated reason.
 *
 * The FLOOR is not shared, and this said it was until @Codex's P2 on
 * `d881f5b`: grouping refuses one group because one group is not a grouping,
 * while one option is still a choice a person can make. What a chooser refuses
 * is NONE. See `consider`.
 *
 * This is shape-derived rather than kind-derived, which is why it is a set
 * computed per generation instead of a row in `MUTATION_FOR_KIND`. A pool
 * whose statuses proliferate stops offering the chooser on its own.
 */
function chooseableEnums(
  shape: ObjectShape,
  params: ResolvedParams,
  /* The number of options the control will hold, per role — task `0215`.
     Named for what it counts and not for where it came from: it was
     `declared`, a count of the schema's list, and a caller that can see both
     sets now sends their union instead. */
  optionCount: Readonly<Record<string, number>> = {},
): ReadonlySet<Role> {
  const out = new Set<Role>();
  // Unknown cardinality is not "small". A source that never counted this role
  // cannot say the set is closed, and guessing yes here would put a truncated
  // dropdown in front of somebody.
  /* AND NEITHER IS ZERO — @Codex's P2 on `d881f5b`. The threshold above is an
     UPPER bound, and the docstring says it is reused because grouping asks the
     same question. Only half of it was: `groupable` tests
     `>= groupableCardinalityMin && <= max`, and this tested the max alone, so a
     count of 0 sailed through and a role with NO options became a chooser —
     a dropdown a person opens onto nothing. It is `0193`'s finding again, in
     the vocabulary rather than in the layout: a control that EXISTS is not a
     control that works.

     Reachable by construction rather than in theory. An enum declared with no
     options and no stored values is absent from `shape.roles` — the shape
     counts POPULATED values — so it arrives only through `optionCount`, where
     `observed` is `undefined` and the offered count is 0.

     The bound here is 1 and not `groupableCardinalityMin`, and that is the
     part the docstring above had wrong rather than a number chosen freely.
     "One group is not a grouping" is true; one OPTION is still a choice a
     person can make on an unset field. The two questions share a ceiling and
     not a floor. */
  /* AND THE DECISION IS MADE ONCE, FROM THE FINAL COUNT — @Codex's P2 on
     `48121a7`. Both sources used to be read in two loops that each called
     `consider`, and `consider` can only ADD. So an enum with four observed
     values and a union of thirteen was admitted by the first loop, and the
     second loop — computing the real number, finding it over the ceiling —
     could only decline to add a role that was already in. Generation emitted a
     `set-enum` chooser holding all thirteen: the oversized dropdown this
     ceiling exists to prevent, through the door that the fix for the opposite
     case opened.

     A `Set` has no "and take it back out" that is safe to write here — a
     `delete` in the second loop would be correct today and would silently
     stop being correct the moment a third source is added below it. So the
     loops COLLECT and `consider` is the single writer, which is the choke
     point rather than a longer list of places that must agree. */
  const consider = (role: Role, distinct: number | undefined): void => {
    if (distinct === undefined || distinct < 1) return;
    if (distinct > params.groupableCardinalityMax) return;
    out.add(role);
  };
  const distinctOf = new Map<Role, number>();
  const collect = (role: Role, distinct: number | undefined): void => {
    if (distinct === undefined) return;
    const had = distinctOf.get(role);
    distinctOf.set(role, had === undefined ? distinct : Math.max(had, distinct));
  };
  for (const r of shape.roles) {
    if (r.kind !== 'enum') continue;
    collect(r.role, shape.enumCardinality?.[r.role]);
  }
  /* AND THE DECLARED ONES, which the loop above cannot reach — task `0202`.
     A cleared enum is not in `shape.roles` at all: the shape counts populated
     values, so clearing the last one takes the role out of the list this
     iterates and out of `shape.enumCardinality` with it. Walking the shape
     could never see it however the count were read, which is why the request
     carries a second source rather than this function reading harder.

     The kind comes from the ROLE, not from a shape entry, for the same reason:
     there is no entry. `ROLE_KIND` is the vocabulary's own answer and is what
     `bindingFor` two functions down already asks. */
  /* AND THE LARGER OF THE TWO WHERE BOTH EXIST — @Codex's P2 on `e5ffb92`.
     This walked `declared` unconditionally, so a populated enum whose observed
     values EXCEED the threshold — correctly withheld by the loop above — was
     added straight back by a smaller declared option list. The demo then
     unions the declared options with every observed extra when it fills the
     control (`choicesFor` in `main.ts`), so the dropdown a person opened held
     the observed count after all: the exact oversized control this threshold
     exists to prevent, reintroduced by the fix for a different case.

     `distinctFor` is the count that decides, and it is the larger of the two
     sources rather than whichever was read last. That covers the case this
     loop was written for — a cleared enum, absent from `shape.roles` and from
     `shape.enumCardinality`, where the observed count is `undefined` — and the
     case @Codex found, where both exist and the observed one is the real
     size.

     AND `0215` CLOSED THE GAP THIS PARAGRAPH USED TO NAME. It read: a lower
     bound and not the answer, because `choicesFor` offers the UNION of the two
     sets, so five declared and five observed that barely overlap fill a
     control of ten while max says five. Two counts cannot be unioned — so the
     count stopped being the declared one. The host holds both sets and now
     sends their union, which is the number of options the control will hold.

     The floor STAYS. It is no longer the fix, it is the fallback: a caller
     that sends a declared count rather than a union — the old shape, and all
     this interface can require — gets the pre-0215 lower bound rather than a
     number below the observed size. Where the union arrives it dominates the
     observed count and max returns it unchanged. */
  for (const [key, offered] of Object.entries(optionCount)) {
    const role = key as Role;
    if (ROLE_KIND[role] !== 'enum') continue;
    collect(role, offered);
  }
  for (const [role, distinct] of distinctOf) consider(role, distinct);
  return out;
}

/**
 * A binding, directional by construction. `read` is what a role gets when the
 * vocabulary has no way to express writing it — never a default nobody chose.
 *
 * **Exported because a host builds specs too.** `apps/demo`'s capability
 * requests — the document, the graph, the plot — are specs assembled by hand
 * beside the generated one, and every binding in the document's was written
 * `direction: 'read'` with no reason given. That is not a policy, it is a
 * default nobody chose, and it made the document view permanently unwritable
 * whatever the shape underneath it could support. One function answering *"what
 * may be written here"* is the same argument that put `MUTATION_ROLE_KINDS` in
 * `@morph/spec` rather than leaving a copy in the store.
 */
export function bindingFor(slot: string, role: Role, choose: ReadonlySet<Role> = EMPTY): TBinding {
  const mutation = ROLE_KIND[role] === 'enum'
    ? (choose.has(role) ? 'set-enum' as const : undefined)
    : MUTATION_FOR_KIND[ROLE_KIND[role]];
  return mutation === undefined
    ? { slot, role, direction: 'read' }
    : { slot, role, direction: 'read-write', mutation };
}


/**
 * Rank roles for prominence.
 *
 * Derived from role KIND and coverage rather than a hand-written priority
 * table. A media role leads because media is the highest-bandwidth thing on a
 * card; identity leads text because a card without a name is unusable. Those
 * are facts about rendering. Which of the remainder a person wants is the taste
 * model's business, and this function has no opinion about it.
 */
/**
 * Can a learned promotion change this role's place in the compiled spec?
 *
 * `rankRoles` gives media 100 and title/subtitle 90 *before* it consults
 * `prominentRoles`, whose band is 80 — those three are structural and stay
 * ahead on purpose. So promoting one persists a belief, reports it in the
 * taste readout, and compiles to exactly the same layout.
 *
 * @Codex found this on the Promote delivery and it is the shape decision 0036
 * withdrew `role.extent` for: a gesture with no compilation target. Exported
 * rather than duplicated in the demo, because the weights it depends on are
 * ten lines below and would drift apart.
 */
/* AND `!== 'media'` LETS `av` THROUGH, which is task `0227` and is HALF of one
   decision written in two places. `ROLE_KIND['media.video']` is `av`, so a
   recording is not excluded here and `weight` below ranks it 50 rather than
   100 — the two together are why Promote is honestly offered for a recording
   today, and they are true or false together.

   Do not "fix" either one alone. Making a recording rank 100 makes Promote
   useless for it, and `promotionChangesRanking` would then withdraw the
   gesture by itself because it runs the ranking rather than reading the
   weights; excluding `av` here without the weight would withdraw a gesture
   that currently compiles to a different layout. `0227` has the measurement
   and the open question, which is a LOOKING one: does a card led by a player
   read worse than one led by its poster. */
export const promotionCanCompile = (role: Role): boolean =>
  ROLE_KIND[role] !== 'media' && role !== 'title' && role !== 'subtitle';

/**
 * Would promoting this role change the order for THIS shape?
 *
 * `promotionCanCompile` is a fact about the role alone and is necessary but
 * not sufficient. @Codex, on the shape-dependent half: *"with an equal-coverage
 * `title`/`date`/`author` shape, `date` already precedes `author`, so promoting
 * it produces a byte-identical spec; a lone `media.video` likewise remains the
 * sole carded visual child."* The demo would still expose Promote, persist
 * three events and report the belief — the same belief-with-no-compilation-
 * target defect, one level down.
 *
 * Answered by running the ranking both ways rather than by reasoning about the
 * weights, so it cannot drift from them.
 */
export function promotionChangesRanking(
  shape: ObjectShape, role: Role, overrides: ParamOverrides = {},
): boolean {
  if (!promotionCanCompile(role)) return false;
  const params = resolveParams(overrides);
  return rankRoles(shape, params).join('\u0000')
    !== rankRoles(shape, params, [role]).join('\u0000');
}

function rankRoles(shape: ObjectShape, params: ResolvedParams,
  prominent: readonly Role[] = []): Role[] {
  const promoted = new Set(prominent);
  const weight = (r: Role): number => {
    const kind = ROLE_KIND[r];
    /* `media` and not `isVisual`, so `av` falls to 50 with everything else —
       task `0227`, and the docstring at the top of this file warns about
       exactly this shape ("a trailer would have fallen through every one of
       them and been ranked as an ordinary text field"). It is not settled
       whether that is wrong: a still is read at a glance and a film is a
       transport somebody must start. What IS settled is that this line and
       `promotionCanCompile` above move together or not at all. */
    if (kind === 'media') return 100;           // highest bandwidth on a card
    if (r === 'title' || r === 'subtitle') return 90;  // a card without a name is unusable
    if (promoted.has(r)) return 80;                    // learned lead metadata
    if (r === 'rank' || r === 'confidence') return 70; // ordering roles order the view
    if (kind === 'ref') return 30;              // relations are secondary by nature
    return 50;                                  // everything else, by coverage
  };
  return shape.roles
    .filter((r) => r.coverage >= params.roleCoverageFloor)
    .slice()
    .sort((a, b) => weight(b.role) - weight(a.role) || b.coverage - a.coverage)
    .map((r) => r.role);
}

/**
 * Media in the broad sense: a still, a recording OR a document.
 *
 * Adding `av` to the vocabulary typechecked cleanly and would still have been
 * wrong, because every decision in this file compares `=== 'media'` by VALUE.
 * A trailer would have fallen through every one of them and been ranked as an
 * ordinary text field — rendering a blob reference as a line of prose, which is
 * the exact bug this file already carries a comment about for poster frames.
 * Named once so the next kind added cannot repeat it.
 *
 * **That last sentence was true of this file and false of the project**, and
 * @Codex found the gap twice. The list was correct here, `doc` included, while
 * both `deriveShape` implementations kept their own copy written out by value
 * and both omitted `doc` — so a pool of PDFs derived `hasMedia: false` and was
 * typeset as prose. A `const` no other package can import is not named once,
 * whatever its docstring says. The set now lives in `@morph/spec` and this is
 * a lookup rather than a second opinion.
 */
const isVisual = (role: Role): boolean => isVisualKind(ROLE_KIND[role]);

/** Kinds that resolve to WORDS, and so can name a card. Mirrors `NAMING_KINDS`
 *  in `a11y.ts` — the floor is what decides, and offering `select` where the
 *  floor will refuse it only moves the failure later. */
const NAMES = new Set(['text', 'enum', 'date', 'number']);

/**
 * The derivative pairs a CARD may collapse — same kind on both sides.
 *
 * Read from the vocabulary rather than restated, so a third derivative role
 * reaches this without an edit here, and then filtered by kind, which is the
 * part that took a second look.
 *
 * `media.thumb` and `media.hero` are both `media`: two stills, one cheap, and
 * drawing the cheap one instead loses nothing. `media.poster-frame` and
 * `media.video` are NOT — `poster-frame` is `media` and `video` is `av`. A
 * poster is a picture of a recording and cannot be played, so collapsing that
 * pair would take the transport off every film and leave a still where a person
 * expects a player.
 *
 * Caught by asking what else the collapse would reach rather than by a test:
 * every clause was green with `media.video` included, because the fixtures for
 * `0131` are stills. `e2e/smoke.mjs` would have found it in a browser, which is
 * the expensive place to find it.
 *
 * `pickHero` is unaffected and correctly so: it chooses ONE picture for a grid,
 * where a poster frame is exactly the right thing to show for a film.
 */
const DERIVATIVE_SOURCES: ReadonlyArray<readonly [Role, Role]> =
  Object.entries(DERIVATIVE_OF)
    .map(([d, src]) => [d as Role, src as Role] as const)
    .filter(([d, src]) => ROLE_KIND[d] === ROLE_KIND[src]);

/**
 * The media role a card draws, and — for a COLLECTION — the cheap one.
 *
 * @Codex, P1 on #184: *"For a collection of ingested still images, the source
 * and thumbnail are written on exactly the same objects and therefore have
 * equal coverage, but the source is inserted first … generated cards continue
 * binding `media.hero` and loading the full source blob instead of the new
 * `media.thumb` … a collection can still fetch many source images of up to the
 * 64 MiB ingest limit."*
 *
 * Correct, and it is task 0118 half-built: ingestion makes the derivative and
 * nothing asks for it, so the producer exists and the consumer does not. The
 * sort is by coverage and `Array.sort` is stable, so with equal coverage the
 * manifest order decides — and `media.hero` is written first.
 *
 * A SINGLE object keeps the source. Someone looking at one picture wants the
 * picture; the derivative exists so that fifty cards do not each fetch one.
 * That is the whole distinction, and it is why this reads `cardinality` rather
 * than preferring the thumbnail everywhere.
 */
/** A derivative role whose `DERIVATIVE_OF` source is absent from this shape.
 *  The poster of a video that is not there stands in for nothing — task
 *  `0162`'s mechanism, and a fact only the shape can answer. */
const orphanDerivative = (role: Role, shape: ObjectShape): boolean => {
  const source = DERIVATIVE_OF[role];
  return source !== undefined
    && !shape.roles.some((r) => r.role === source && r.coverage > 0);
};

function pickHero(
  shape: ObjectShape, params: ResolvedParams, allowed: ReadonlySet<Role>,
): Role | undefined {
  const media = shape.roles
    // `allowed` for the same reason `pickGroupBy` takes it, and this is the
    // THIRD picker caught reading the raw shape past suppression. @Codex, P2 on
    // #184: *"After a person repeatedly hides a collection thumbnail,
    // `manualTaste` places `media.thumb` in `suppressedRoles` and the generator
    // removes it from `ranked`, but this new special case selects the thumbnail
    // directly from the raw shape anyway. Since ingestion adds `media.hero`
    // before `media.thumb`, the thumbnail would not otherwise have been
    // selected; this branch specifically causes the next regeneration to bring
    // the explicitly hidden role back."*
    //
    // The filter goes on the whole list rather than on the thumbnail branch,
    // which is where they found it. `media[0]` had the same hole and always
    // did — the branch made it reachable by a role somebody had just hidden,
    // which is what turned a latent bug into a visible one.
    .filter((r) => allowed.has(r.role))
    .filter((r) => ROLE_KIND[r.role] === 'media' && r.coverage >= params.heroCoverageFloor)
    /**
     * Coverage first — and on a coverage TIE, a DERIVATIVE loses to a source.
     *
     * Task `0162`, from the fabric blind drive: a person added a photo to an
     * opened film, and the single-object lens kept painting the seeded
     * `media.poster-frame` URL. The mechanism, measured: on that shape every
     * media role ties at coverage 1, a bare coverage sort is stable, so
     * `media[0]` was MANIFEST INSERTION ORDER — the seeded poster def
     * predates the minted hero, and the person's own picture lost to a
     * derivative of a video that is not even on the object.
     *
     * The first fix demoted only ORPHAN derivatives — a `DERIVATIVE_OF` role
     * whose source is absent from the shape. @Codex's P2 on `1cfe1ae` showed
     * the precondition was doing no work: put the video back on the object
     * and the poster is nobody's orphan, the sort is stable again, and the
     * person's freshly attached hero is unreachable from the very lens that
     * acknowledged the attachment. So the rank is derivative-ness itself,
     * with orphanhood behind it for ties between two derivatives. A
     * derivative stays USABLE (a legacy pool whose only media is a seeded
     * poster still gets its hero; the films table has been exactly that
     * since the seeds were written) — it only stops beating a present
     * source on a tie. Tie-break only, deliberately: a derivative at 1.0
     * still outranks a source at 0.85, because the grid-uniformity findings
     * above this function are about coverage and this rule must not reopen
     * them. The collection branch below is unaffected: it prefers the cheap
     * derivative EXPLICITLY, after this sort, which is the correct opposite
     * preference for the many-objects case.
     */
    .sort((a, b) => b.coverage - a.coverage
      || Number(DERIVATIVE_OF[a.role] !== undefined) - Number(DERIVATIVE_OF[b.role] !== undefined)
      || Number(orphanDerivative(a.role, shape)) - Number(orphanDerivative(b.role, shape)));
  if (shape.cardinality !== 'single') {
    /**
     * Either derivative, not only the still one — and today that is belt over
     * braces rather than a fix.
     *
     * @Codex, P1 on #184, said this branch *"only prefers `media.thumb`, so it
     * falls through to `media[0]` and binds the full video instead of its
     * poster frame; a collection can therefore load dozens of source videos of
     * up to 64 MiB each."* **It does not reproduce**, and the reason is one
     * line in `vocabulary.ts`: `ROLE_KIND['media.video']` is `'av'`, not
     * `'media'`, so `media.video` is filtered out of this list before the
     * branch is reached and can never be `media[0]`. Probed on the exact shape
     * described — a `large-set` with `media.video` and `media.poster-frame` at
     * equal coverage, source first — and the compiled spec binds
     * `media.poster-frame` with or without the poster-frame name here.
     *
     * Kept anyway, because the finding is right about the SHAPE of the rule
     * even where it is wrong about today's outcome: `derivativeRoleFor` emits
     * two roles and this is their consumer, so naming one of them was a rule
     * written about an instance. It costs nothing and it stops depending on a
     * kind assignment in another package to be correct.
     */
    /**
     * ...and only when it covers as much of the collection as the source does.
     *
     * @Codex, P2 one round later: *"When a collection contains source media on
     * every object but thumbnails on only 80-99% — for example, an upgraded
     * vault with legacy source-only images or files whose derivation failed —
     * this branch selects `media.thumb` merely because it clears the 0.8 floor,
     * even though the sorted `media.hero` entry has higher coverage. The
     * generated cards then go blank for every object without a derivative,
     * contradicting the stated invariant that an intermittent hero must not
     * make the grid ragged."*
     *
     * The floor is a threshold and the list is SORTED, so the branch was
     * reaching past a better answer to take one that merely qualified. `>=`
     * rather than `>` keeps the case this branch was written for: a derivative
     * that covers exactly what its source does is the one to bind, because it
     * is the cheap picture of the same population.
     *
     * `0118` clause 6 is why the fallback is a source rather than a blank: a
     * file whose derivation failed still has its source stored, and showing it
     * is better than showing nothing.
     */
    /**
     * ...and only when it CONTAINS the source, not merely matches its coverage.
     *
     * @Codex, P2 on `384b5df`: *"When the source and thumbnail both clear the
     * hero floor with equal marginal coverage but do not cover the same
     * objects — for example, each is present on a different 85% subset — this
     * comparison selects `media.thumb` and the gallery binds only that role,
     * leaving source-only objects blank. The new `shape.derivativeCovers` field
     * exists specifically because equal coverage does not prove containment, so
     * prefer the derivative over a present source only when that field confirms
     * the derivative covers it; absent or false containment must retain the
     * source."*
     *
     * Task `0131` put `pickHero` under *"Not in scope"* and said why: the
     * consequence *"there is bounded — it picks one hero for a grid whose
     * uniformity is already the point, and `0118` clause 6 keeps a source
     * visible when its derivative is missing"*. That reasoning is wrong about
     * this path. `0118` clause 6 covers a role that is ABSENT from the shape;
     * two roles at 85% on disjoint subsets are both present, both clear the
     * floor, and the gallery then binds one of them and leaves the other's
     * objects blank.
     *
     * The same sentence `0131` closed on, one function along: *"two coverages
     * of 0.5 are equally consistent with total overlap and with none."* The
     * field was built for exactly this question and this was the second caller
     * that needed it.
     *
     * Absent reads as NOT KNOWN and keeps the source, which is the safe
     * direction and the same rule the card collapse follows.
     */
    const covered = shape.derivativeCovers ?? {};
    const derived = media.find((r) =>
      r.role === 'media.thumb' || r.role === 'media.poster-frame');
    if (derived && covered[derived.role] === true
      && derived.coverage >= (media[0]?.coverage ?? 0)) return derived.role;
    /**
     * The fallback must not reinstate what the line above just declined.
     *
     * @Codex, P2 on `10bcd23`: *"When `media.video` covers a collection but
     * `media.poster-frame` covers only 80-99% of it, such as after some poster
     * derivations fail, the source is excluded from `media` because its kind is
     * `av`; this containment check declines the poster, but the immediate
     * fallback still returns that same poster as `media[0]`. `buildCollection()`
     * then enters the hero path and omits all carded visual roles, leaving
     * every source-only object blank."*
     *
     * The `av` kind is what makes it invisible: `media.video` is filtered out
     * of this list before either branch sees it, so a video pool's `media[0]`
     * IS the poster. Declining it and then returning it is the same decision
     * twice with opposite answers.
     *
     * **KNOWN-false only, and the first draft of this was wrong about that.**
     * It fired on `undefined` too, reasoning that absent means NOT KNOWN and
     * the paragraph above says absent *"keeps the source"*. Six clauses went
     * red, and the one that matters is `shape.test.ts`'s *"prefers a POSTER
     * FRAME for a collection of videos, not the clips"* — which deliberately
     * picks the poster when containment is unknown, because the alternative is
     * the card path binding dozens of 64 MiB clips. That was @Codex's earlier
     * P1 and it outranks a blank card.
     *
     * So the two findings do not conflict once the third state is respected:
     *
     * ```
     * covered === true    the poster IS the hero          (the branch above)
     * covered undefined   still the hero — a partial      unknown is not a
     *                     poster beats loading the clips  reason to load 64 MiB
     * covered === false   no hero — some objects have no  THIS guard
     *                     poster and would draw blank
     * ```
     *
     * `undefined` rather than another role: with the source filtered out by
     * kind there is nothing else to pick, and no hero is what sends
     * `buildCollection` down the card path, which draws the videos AND the
     * posters that exist. A hero is one picture for a grid; a derivative KNOWN
     * to miss some objects cannot be that picture.
     */
    if (derived && covered[derived.role] === false
      && media[0]?.role === derived.role) return undefined;
  }
  return media[0]?.role;
}


/**
 * Is this role few enough distinct values to read as groups rather than as
 * noise? Twelve columns is already a lot; fifty is a mess.
 *
 * Named once because BOTH groupings ask it. A second copy of this test for the
 * inner one would be two descriptions of a single rule, free to drift apart —
 * which is what `UNGROUPABLE_KINDS` in the vocabulary already records happening
 * to the kind guard.
 */
function groupable(shape: ObjectShape, params: ResolvedParams, role: Role,
  coverage: number): boolean {
  if (ROLE_KIND[role] !== 'enum') return false;
  if (coverage < params.roleCoverageFloor * 2) return false;
  const distinct = shape.enumCardinality?.[role];
  return distinct !== undefined
    && distinct >= params.groupableCardinalityMin
    && distinct <= params.groupableCardinalityMax;
}

/**
 * Only group by something groupable, and never by a role the user hid.
 *
 * SUPPRESSION is the reason this takes `allowed` rather than reading
 * `shape.roles` alone. `TastePriors.suppressedRoles` is documented as *"honoured
 * absolutely — an explicit hide that keeps coming back is the fastest way to
 * lose a user"*, and `propose` filters `ranked` by it. Both pickers read the
 * shape directly, so a suppressed role could still head every column: hidden
 * from the cards and printed above them. @Codex found it in `pickSubGroupBy`;
 * it was in `pickGroupBy` too, where the consequence is larger.
 */
function pickGroupBy(shape: ObjectShape, params: ResolvedParams,
  allowed: ReadonlySet<Role>): Role | undefined {
  return shape.roles.find(
    (r) => allowed.has(r.role) && groupable(shape, params, r.role, r.coverage),
  )?.role;
}

/**
 * A slot's visible name, from the role.
 *
 * `ViewStateSlot.name` is required and is the only string the generator has to
 * invent. Derived from the vocabulary term rather than from any value, so the
 * pooled spec carries nothing about the pool's contents.
 *
 * **AND THE CAPTION A PERSON READS IS THIS, NOT THE POOL'S OWN LABEL.** The
 * first version of this comment said the host's label reaches the renderer
 * through `RenderInput.labels` — it does not: `render-html.ts` prints
 * `slot.name` directly. So the Films control reads "Date" where every other
 * field in that pool reads "Released", and Notes and Tasks lose their declared
 * date labels the same way. @Codex measured it.
 *
 * FIXED in the renderer, not here, and the division is the point: this stays
 * content-free because a pooled spec must be, and `render-html.ts` resolves the
 * caption from `RenderInput.labels` at draw time with this as the fallback. A
 * host that declares no label still gets what it got before. Task 0087.
 */
const nameOfRole = (role: Role): string => {
  const last = role.split('.').pop() ?? role;
  return last.charAt(0).toUpperCase() + last.slice(1).replace(/-/g, ' ');
};

/**
 * One region of the split: the same collection, with its own predicate and its
 * own id.
 *
 * The ID is what makes this work at all. A host queries per region and names
 * each result set back by the collection's authored id — @Codex's region-key
 * ruling on 0081 — so two regions sharing one id would be two regions sharing
 * one answer, which is the silent re-pairing the whole design is arranged
 * against.
 */
type CollectionQuery = { filter?: unknown };

/** The two branch names, used by `partitioned` to rename each subtree AND by
 *  the emitted `partition` to name them. One source, so a rename cannot leave
 *  the declaration pointing at a node that no longer exists. */
const BRANCH_A = 'above';
const BRANCH_B = 'below';

function partitioned(
  collection: TNode, id: string, filter: NonNullable<CollectionQuery['filter']>,
): TNode {
  // A DEEP COPY, not a spread. Both regions are the same collection, and a
  // shallow spread leaves them sharing every nested object — which the spec's
  // own cycle detector reports as `cyclic — value contains a cycle`, refusing
  // the whole lens. Two regions that are the same tree must be two trees.
  const c = structuredClone(collection) as TNode & {
    id: string; query?: Record<string, unknown>;
  };
  // EVERY id in the subtree, not only the collection's. `validate.ts` refuses
  // `duplicate-id` across the whole spec, and both regions carry a card called
  // `item` — measured as
  // `/root/children/1/items/0/spec: duplicate-id — id "item" already used`.
  // A copy of a tree is a copy of its names too.
  const rename = (value: unknown): void => {
    if (!value || typeof value !== 'object') return;
    if (Array.isArray(value)) { for (const v of value) rename(v); return; }
    const node = value as Record<string, unknown>;
    if (typeof node.id === 'string') node.id = `${node.id}-${id}`;
    for (const v of Object.values(node)) rename(v);
  };
  rename(c);
  return {
    ...c,
    id: `collection-${id}`,
    query: { ...(c.query ?? {}), filter },
  } as TNode;
}

/**
 * A CONTROL, and the two regions it drives.
 *
 * `viewState` is the last inert thing in the vocabulary that the generator
 * could not reach: the schema calls a slot *"a named, typed control whose value
 * drives queries in several regions — Coda's sliders and selects"*, the store
 * has resolved `Predicate.valueFrom` from `Scope.viewState` for weeks, and the
 * renderer draws the control. Nothing declared one.
 *
 * @Codex's ruling on 0079 is why this returns a role rather than merely finding
 * one, and it reframed the question I asked:
 *
 * > *"A slot by itself is not the feature: it is an unexplained control. A
 * > predicate in only one region is an ordinary filter box. The schema's Coda
 * > claim becomes true only when one value drives several regions."*
 *
 * So a slot is earned by a ROLE AND A SECOND REGION, and the caller emits both
 * or neither.
 *
 * **The evidence is joint, like `subGroupBy`'s.** Coverage cannot say whether a
 * threshold could partition anything — a role can be on every object and hold
 * one value on every object. `orderedCardinality` is the bounded distinct count
 * that can, and three is the floor: *"two values describe a binary category
 * wearing a numeric encoding; a threshold control adds no expressive power over
 * the two fixed groups already derivable from that shape."*
 *
 * Ordered scalars only. Text and enum are refused because the content-free
 * shape carries no option values and a blank input beside `equals` is a guess
 * about search intent; `ref` because the host already refuses to fake an
 * ObjectId picker with a label input; media, av and doc because their legal
 * predicates are presence tests that need no value at all.
 */
const SLOT_OP: Partial<Record<RoleKind, 'greater-than' | 'after'>> = {
  number: 'greater-than', score: 'greater-than', date: 'after',
};

function pickViewStateSlot(
  shape: ObjectShape, params: ResolvedParams, ranked: readonly Role[],
): { role: Role; op: 'greater-than' | 'after' } | undefined {
  // RANKED ORDER, not `shape.roles`. The ruling says *"use the first in the
  // same allowed ranked-role order the rest of the generated lens uses. Do not
  // create a second priority table for controls."* I passed `ranked` as a
  // membership SET and then iterated coverage order, which is a second
  // priority table wearing the first one's name — measured by @Codex on the
  // films fixture, where `rankRoles` promotes `score` and this picked `date`.
  for (const role of ranked) {
    const r = shape.roles.find((x) => x.role === role);
    if (!r) continue;
    const op = SLOT_OP[ROLE_KIND[role]];
    if (!op) continue;
    if (r.coverage < params.roleCoverageFloor) continue;
    const distinct = shape.orderedCardinality?.[role];
    // ABSENT IS NOT EVIDENCE. A shape derived by a path that does not compute
    // the statistic withholds the control rather than guessing at it — the same
    // rule `pickSubGroupBy` follows for its joint count.
    // THREE, inline. @Codex: *"two values describe a binary category wearing a
    // numeric encoding; a threshold control adds no expressive power over the
    // two fixed groups already derivable from that shape."* Not a parameter
    // while `splitOnViewState` ships off — a floor behind a switch reaches no
    // shipped layout, and `params.ts`'s own test refuses one that cannot.
    if (distinct === undefined || distinct < 3) continue;
    return { role, op };
  }
  return undefined;
}

/**
 * The SECOND grouping, inside each column of the first.
 *
 * Notion's board and table both sub-group, and so does Coda. `subGroupBy` has
 * been in the schema, validated with a rule of its own, projected into the
 * pooled genome and — since it started drawing — rendered. It was emitted by
 * nothing, so no generated lens could have one.
 *
 * `groupable` is the same test the outer grouping applies, because a groupable
 * role is a groupable role. TWO further conditions are this one's own, and the
 * second is the interesting one.
 *
 * **Not the role the outer grouping took.** A board sub-grouped by its own
 * column heading draws one sub-section per column containing everything in it.
 *
 * **And it has to actually DIVIDE those columns.** Marginal counts cannot say
 * whether it does: two roles can each hold four values and never cross — every
 * `Open` task in Store, every `Done` one in Runtime — and both pass every test
 * above. The renderer suppresses a lone sub-group, correctly, so the board
 * would draw no sub-heading at all while the spec that asked for one still
 * differs in the pooled genome the taste model learns from. That is precisely
 * the defect task 0079 exists to close, reintroduced by the thing closing it.
 * @Codex caught it.
 *
 * `enumPairCardinality` is the joint count. If the outer role holds `O`
 * distinct values and the pair holds exactly `O`, then every column has one
 * inner value and nothing divides; more than `O` means at least one column
 * splits. That is the renderer's own suppression rule, asked of the shape
 * before the spec is written.
 *
 * ORDER follows `shape.roles`, which both derivation paths sort by coverage —
 * so the outer grouping gets the better-covered role and the inner one the
 * next. A consequence of asking in order, not a preference encoded here.
 */
function pickSubGroupBy(
  shape: ObjectShape, params: ResolvedParams, outer: Role | undefined,
  allowed: ReadonlySet<Role>,
): Role | undefined {
  if (outer === undefined) return undefined;   // nothing to sub-group inside
  const columns = shape.enumCardinality?.[outer];
  if (columns === undefined) return undefined;
  return shape.roles.find((r) => {
    if (r.role === outer || !allowed.has(r.role)) return false;
    if (!groupable(shape, params, r.role, r.coverage)) return false;
    const pairs = shape.enumPairCardinality?.[enumPairKey(outer, r.role)];
    return pairs !== undefined && pairs > columns;
  })?.role;
}

/** Snap a continuous appetite to the nearest legible density state. Discrete by
 *  construction — a float would produce valid, unreadable layouts. */
function nearestDensity(appetite: number): Density {
  return DENSITIES.reduce((best, d) =>
    Math.abs(DENSITY_LEVEL[d] - appetite) < Math.abs(DENSITY_LEVEL[best] - appetite) ? d : best);
}

/**
 * Score each type system against the shape rather than looking one up.
 *
 * The systems declare their own measurable character in the vocabulary, so this
 * is derivation from data rather than a table of preferences.
 */
function pickTypography(shape: ObjectShape, ctx: UseContext, appetite: number): TypeSystem {
  const textRoles = shape.roles.filter((r) => ROLE_KIND[r.role] === 'text').length;
  const mediaRoles = shape.roles.filter((r) => isVisual(r.role)).length;
  const total = Math.max(1, shape.roles.length);

  const need = {
    prose: textRoles / total,
    density: appetite,
    media: shape.hasMedia ? mediaRoles / total + 0.4 : 0,
    editing: ctx === 'authoring' ? 1 : 0,
  };

  let best: TypeSystem = TYPE_SYSTEMS[0];
  let bestScore = -Infinity;
  for (const t of TYPE_SYSTEMS) {
    const c = TYPE_SYSTEM_CHARACTER[t];
    const score = need.prose * c.proseFit + need.density * c.densityFit
                + need.media * c.mediaFit + need.editing * c.editingFit;
    if (score > bestScore) { bestScore = score; best = t; }
  }
  return best;
}

/**
 * Information appetite on 0..1, blended from the cold-start prior and what the
 * shape actually is. The prior is held at `priorMass` so a handful of real
 * signals overrule it — see params.ts.
 */
function appetiteFor(req: GenerationRequest, params: ResolvedParams): number {
  const prior = params.contextDensityAppetite[req.context] ?? 0.5;

  // Evidence from the shape itself, which is not taste: a large media-light
  // collection genuinely needs to be scannable, and a single object does not.
  const signals: number[] = [];
  if (req.shape.cardinality === 'large-set') signals.push(req.shape.hasMedia ? 0.45 : 0.7);
  if (req.shape.cardinality === 'single') signals.push(0.3);
  if (req.shape.roles.length > 6) signals.push(0.65);

  const m = params.priorMass;
  const evidence = signals.reduce((a, b) => a + b, 0);
  return (prior * m + evidence) / (m + signals.length);
}

/**
 * Which folds a collection should offer, derived from the shape.
 *
 * Nobody authors a rollup in Morph — the same rule that makes a media-heavy
 * pool into a gallery makes a pool with a numeric column offer its total.
 *
 * The function is derived from the role, and the one distinction worth making
 * is `score`: a SUM of ratings is meaningless ("47 stars") while an average is
 * exactly what a person wants. Everything else numeric sums.
 *
 * Gated on `roleCoverageFloor` rather than a new parameter, and for the same
 * reason that floor exists: a total over a column two thirds of the objects
 * leave empty is a number that invites a wrong conclusion. If the role is too
 * sparse to surface, it is too sparse to fold.
 *
 * Derived from the roles the collection actually SHOWS, not from the shape.
 *
 * Two versions of this were wrong in the same direction. The first read
 * `shape.roles` and handed a rollup to a role the user had SUPPRESSED — taste
 * overruled by inference, in the one file whose job is to let taste win. The
 * second read `ranked`, which respects taste but includes roles that never
 * become columns: the store dutifully computed `quantity: sum = 7890` for the
 * notes pool and the table drew nothing, because there was no Words column to
 * put it under.
 *
 * A total belongs under the column it totals. If the role is not on screen,
 * the fold has nowhere to go and asking for it is work nobody can see.
 */
function pickAggregates(
  shape: ObjectShape, shown: readonly Role[], params: ResolvedParams,
): { role: Role; fn: 'sum' | 'avg' }[] {
  const allowed = new Set(shown);
  return shape.roles
    .filter((r) => allowed.has(r.role)
      && (r.kind === 'number' || r.kind === 'score')
      && r.coverage >= params.roleCoverageFloor)
    .map((r) => ({ role: r.role, fn: r.kind === 'score' ? 'avg' as const : 'sum' as const }));
}

/**
 * A LOOKUP column: a value read from the object a row REFERENCES.
 *
 * Coda's lookup, derived from shape rather than authored — the same rule that
 * makes a numeric column offer its total makes a pool of linked objects offer
 * what they are linked TO.
 *
 * ## Why only a PEER relation
 *
 * The generator has to know what roles exist on the far side of the reference,
 * and `ObjectShape` describes this pool and no other. For `source` — a
 * reference into another document — the far roles are genuinely unknown, and
 * guessing would produce a column headed by a role the target may not carry.
 *
 * A PEER reference points at a sibling in the same collection, so the far
 * side's roles ARE this shape's roles. That is not a convenience; it is the
 * only case where the inference is sound, and it is why `source` is excluded
 * here rather than handled worse. Extending this to `source` needs the shape to
 * carry the referenced pool's roles, which is a `store/src/shape.ts` change
 * (the same split as `crossDocDensity`) and its own task.
 *
 * ## Which role to read
 *
 * A scalar, and not the naming role. Reading the linked object's NAME would
 * duplicate the reference chip beside it — the chip already says which object
 * it is — so the useful lookup is a different property: how long the linked
 * note is, what state it is in. Highest coverage wins, and the coverage floor
 * applies for the reason it applies to a fold: a column two thirds empty
 * invites a wrong conclusion.
 *
 * `allowed` rather than `shape.roles`, so a role the user SUPPRESSED cannot
 * come back through the far side of a relation. That is the mistake
 * `pickAggregates` records making twice, and it would be worse here — a hidden
 * property reappearing under a heading naming somebody else's row.
 */
function pickLookup(
  shape: ObjectShape, allowed: ReadonlySet<Role>, params: ResolvedParams,
): { role: Role; lookup: Role } | undefined {
  const peer = shape.roles.find((r) => r.role === 'relation.peer'
    && r.coverage >= params.roleCoverageFloor);
  if (!peer) return undefined;
  // `date` is in and `text` is out. A date reads as a column; free text on the
  // far side is a second title competing with the row's own.
  const READABLE: readonly RoleKind[] = ['number', 'score', 'date', 'enum'];
  const read = shape.roles
    .filter((r) => allowed.has(r.role) && READABLE.includes(r.kind)
      && r.coverage >= params.roleCoverageFloor)
    .sort((a, b) => b.coverage - a.coverage)[0];
  return read ? { role: peer.role, lookup: read.role } : undefined;
}

function buildCollection(
  req: GenerationRequest, ranked: Role[], params: ResolvedParams, appetite: number,
): TNode {
  // `ranked` is `shape.roles` minus what taste suppressed, so it is the set
  // every picker is allowed to choose from. Passed rather than re-derived:
  // deriving the suppression test twice is how it came to be missing from one
  // of them — and then from a second, and then from a third.
  const allowed = new Set(ranked);
  const hero = pickHero(req.shape, params, allowed);
  const groupBy = pickGroupBy(req.shape, params, allowed);
  const subGroupBy = pickSubGroupBy(req.shape, params, groupBy, allowed);
  const choose = chooseableEnums(req.shape, params, req.enumOptionCount);
  // Metadata budget scales with appetite rather than branching on context.
  const budget = Math.max(1, Math.round(params.metadataBudgetBase * (0.5 + appetite * 1.5)));
  const metaRoles = ranked
    .filter((r) => r !== hero && !isVisual(r))
    .slice(0, budget);

  // After `metaRoles`, because a fold may only name a role this collection
  // shows — and only when the presentation it will get can DRAW one.
  //
  // A hero means the runtime derives `gallery`, and a gallery has no column to
  // foot and no group header to fill. Asking for the fold anyway made the host
  // rescan the whole scope on every render to produce a number nothing could
  // display: the identical compute-with-nowhere-to-put-it this task was written
  // to eliminate, reintroduced one layer up. Found by @Codex.
  //
  // A board (groupBy) and a table (two or more fields) both consume folds, and
  // those are the two the runtime derives when there is no hero.
  const drawsRollups = !hero && (groupBy !== undefined || metaRoles.length >= 2);
  const aggregate = drawsRollups ? pickAggregates(req.shape, metaRoles, params) : [];

  /**
   * A WORD COUNT of the prose, when the prose itself cannot be a column.
   *
   * Coda's formula column, derived from shape rather than authored, and the
   * pairing both Coda and Notion actually ship: a long-text property beside a
   * formula counting it.
   *
   * It earns its place precisely BECAUSE the prose is usually also a column. A
   * paragraph in a table cell is truncated to one line by the stylesheet, so
   * the cell shows the first few words and gives no sense of whether there are
   * six more or six hundred. The count is the part of the body a cell can
   * actually carry.
   *
   * Only where there is already a table to put it in. A single-object lens
   * shows the prose itself, and a count beside the whole thing is noise.
   */
  const prose = req.shape.roles.find((r) => isProse(r.role));
  const wantsWordCount = !hero && metaRoles.length >= 2 && prose !== undefined;

  // Gated exactly as the word count is, and for the same reason: a lookup is a
  // COLUMN, and a gallery has no columns. Offering one where the runtime will
  // derive `gallery` is the compute-with-nowhere-to-put-it that `drawsRollups`
  // above exists to prevent, one binding further along.
  const lookup = !hero && metaRoles.length >= 2
    ? pickLookup(req.shape, allowed, params) : undefined;

  const bindings: TBinding[] = [
    ...(hero ? [bindingFor('hero', hero, choose)] : []),
    ...metaRoles.map((r, i) => bindingFor(i === 0 ? 'primary' : `meta-${i}`, r, choose)),
    ...(wantsWordCount
      ? [{ slot: 'words', role: prose!.role, direction: 'read' as const,
           derive: 'words' as const }]
      : []),
    ...(lookup
      ? [{ slot: 'lookup', role: lookup.role, direction: 'read' as const,
           lookup: lookup.lookup }]
      : []),
  ];

  /**
   * Media in the CARD, when it cannot be a hero.
   *
   * `heroCoverageFloor` is an invariant at 0.8 and it is right: an intermittent
   * hero makes a grid go ragged, and no taste setting redeems that. But the
   * rule is about GRID UNIFORMITY, and it was being applied as though it were
   * about media at all.
   *
   * The archive pool is the proof. Four objects: two films with a poster, two
   * recordings with none. Poster coverage 0.5, so no hero — correct — and then
   * `isVisual` kept every media and av role out of `metaRoles` as well, so the
   * pool rendered as a three-column table of Title, Date and Author. A
   * media-first collection reporting `hasMedia: true` showed **no media at
   * all**.
   *
   * @Codex found it by driving the deployed page. My own harness missed it
   * because it switches to a single-object lens first, where `buildSingle`
   * emits recordings and everything looks right — the collection view, which is
   * what a person actually arrives at, was never checked.
   *
   * A card in a LIST has no uniformity constraint: a row without a poster is
   * simply a row without a poster. So the media goes in the card, the flow
   * stays `stack`, and `presentationOf` derives `list` rather than `gallery`.
   */
  /**
   * Both roles, and the reason is a limit rather than a preference.
   *
   * @Codex found three defects here in a row and each fix was an inference
   * about CONTAINMENT drawn from a number that cannot carry it:
   *
   * 1. card everything visual — draws one picture twice and fetches the source;
   * 2. collapse whenever the derivative is present — loses the image on every
   *    object that has only a source;
   * 3. collapse when the derivative's coverage is at least the source's — two
   *    coverages of 0.5 are equally consistent with total overlap and none.
   *
   * The only sound rule is to collapse when the derivative covers EVERYTHING,
   * because a role on every object contains every other role's population by
   * arithmetic. And that rule can never fire here: this path is taken only when
   * no media role clears `heroCoverageFloor`, which is an invariant 0.8, so
   * every role reaching it has coverage below 0.8 and none can be at 1.
   *
   * So the collapse was removed rather than corrected a third time. A helper
   * that looks like a rule and cannot fire is worse than the duplicate it was
   * meant to prevent.
   *
   * ## Settled, 2026-08-30 — `ObjectShape` carries the containment now
   *
   * `0131` said what would settle it and this is that: `derivativeCovers` is
   * one boolean per derivative role, computed in the pass both shape derivers
   * were already making, and it answers exactly *"does every object with the
   * source also have the derivative"*.
   *
   * So the collapse is EXACT rather than conservative, and it fires in the case
   * it was written for — a vault where ingestion derived for everything — while
   * declining in the heterogeneous one. The three rejected rules all inferred
   * containment from a comparison; this reads it.
   *
   * `undefined` means the shape does not carry the fact, and it collapses
   * nothing. A shape assembled by something other than the two derivers has the
   * field absent, and a missing key inside it means the source is not present
   * at all — neither is a licence to drop a picture.
   */
  const covered = req.shape.derivativeCovers ?? {};
  const carded = hero
    ? []
    : ranked.filter((r) => isVisual(r)
        && (req.shape.roles.find((x) => x.role === r)?.coverage ?? 0) >= params.roleCoverageFloor)
      // The SOURCE goes when its derivative is on every object that has it.
      // Written as "drop the source", not "keep the derivative", because those
      // differ when the derivative was suppressed or fell below the floor: only
      // this direction leaves the source drawn, which is the safe one.
      .filter((r) => !DERIVATIVE_SOURCES.some(([derivative, source]) =>
        source === r && covered[derivative] === true && ranked.includes(derivative)));

  const children: TNode[] = carded.map((r, i) => ({
    kind: 'media' as const,
    id: `card-av-${i}`,
    binding: bindingFor(`card-av-${i}`, r, choose),
    fit: 'contain' as const,
    // OPTIONAL, because this is one template over a ragged pool: the films
    // carry a poster and no audio, the recordings carry audio and no poster.
    // Without this every object advertised what it did not have — "No Audio"
    // on both films, "No Video" on both recordings, ten media nodes for four
    // objects. Found by @Codex driving the preview.
    optional: true,
    // A still with no aspect is a `<div>` with a width and no height, so the
    // one poster that IS present collapsed to nothing. A recording sizes
    // itself; a background image does not.
    ...(ROLE_KIND[r] === 'media' ? { aspect: '2/3' as const } : {}),
  }));

  /**
   * Prose that the metadata budget could not keep, beside the metadata it did.
   *
   * This began as a transcript-only repair: Archive's three-slot budget went to
   * title, date and author, so a pool whose distinguishing content was prose
   * rendered as metadata ABOUT that prose. Canonical document Bodies expose the
   * same structural rule more sharply. Retiring one legacy scalar can move Body
   * just below four fully populated roles; dropping it then turns a migrated
   * note into a row about its author and word count with no words to read.
   *
   * Prose is card CONTENT, not merely metadata. It remains in the ordinary
   * bindings when the budget selects it, and becomes one bounded card child
   * only when the budget omits it. The exclusion prevents duplicate Body or
   * transcript fields. Coverage and taste still govern `ranked`: this does not
   * resurrect a sparse or explicitly suppressed role.
   */
  const shownMeta = new Set(metaRoles);
  const proseChildren: TNode[] = hero
    ? []
    : ranked
        .filter((r) => isProse(r) && !shownMeta.has(r))
        .map((r, i) => ({
          kind: 'field' as const,
          id: `card-prose-${i}`,
          binding: bindingFor(`card-prose-${i}`, r, choose),
        }));

  // A shape whose only populated role is a recording produced a card with NO
  // bindings: `pickHero` wants a still, and `metaRoles` filters every visual
  // role out. `validateSpec` accepted that card and `compileSpec` then refused
  // the whole lens with `no-accessible-name`, so a perfectly ordinary object
  // shape had no lens at all. Found by @Codex.
  // ...unless a CHILD already shows it. A shape whose only populated role is a
  // recording got the fallback binding AND the media child, and `compileSpec`
  // turned both into media nodes — two identical players on every object.
  // Found by @Codex.
  if (bindings.length === 0 && ranked[0] && !carded.includes(ranked[0])) {
    bindings.push(bindingFor('primary', ranked[0], choose));
  }

  // And `select` is offered only if something in the tile can NAME it. The
  // floor requires an accessible name for an interactive node, and it is right
  // to: a card a screen reader announces as "button" is not usable. Where the
  // content genuinely cannot name itself, the honest answer is a tile that is
  // not selectable — not a tile that claims to be and gets the lens refused.
  const nameable = bindings.some((b) => NAMES.has(ROLE_KIND[b.role]) && b.role !== 'transcript');

  return {
    kind: 'collection',
    id: 'collection',
    flow: hero ? 'grid' : 'stack',
    ...(groupBy || aggregate.length || req.context === 'comparing'
      ? {
          query: {
            ...(groupBy ? { groupBy } : {}),
            // WITH its outer grouping, never alone. `validate.ts` refuses one
            // without the other, and `pickSubGroupBy` returns nothing when
            // `groupBy` is absent — but a generator that relies on a check made
            // in another layer is relying on a check it did not make.
            ...(groupBy && subGroupBy ? { subGroupBy } : {}),
            ...(aggregate.length ? { aggregate } : {}),
            ...(req.context === 'comparing'
              ? { sort: [{ role: ranked[0] ?? 'title', direction: 'asc' as const }] }
              : {}),
          },
        }
      : {}),
    /*
     * AND `navigate` WITH IT — decision 0065, ruled by @Codex on task 0185
     * finding 1.
     *
     * `select` alone was internally inconsistent: `selectFromClick` navigates
     * on any click, so a pointer already reached a row's page, while the
     * declared affordance omitted navigation and the renderer's leading Open
     * control -- which is gated on `navigate` -- never appeared. The ordinary
     * table therefore had a pointer route to a row's page and no keyboard
     * route at all, on the most common table in the product.
     *
     * The ruling's own words: if selecting an object changes the surface
     * subject, that is navigation and must be declared as `navigate`. A look
     * may decide how the gutter is DRAWN and may not decide whether a keyboard
     * user can reach a row's page.
     *
     * `nameable` gates both, for the reason it already gated one: an
     * interactive node needs an accessible name, and where the content cannot
     * name itself the honest answer is a tile that advertises neither.
     */
    ...(nameable ? { affordances: ['select' as const, 'navigate' as const] } : {}),
    /**
     * The split is over roles this card actually DRAWS, not over the shape.
     *
     * `splittingRole` read `shape.roles` alone until the `0131` collapse, and
     * that was harmless while every visual role above the floor was carded.
     * It stopped being harmless the moment a source could be dropped: the
     * source is still in the shape at 0.5 coverage, so it was chosen as the
     * split, and the two branches came out IDENTICAL — a guard that separates
     * nothing, over a role no card mentions, doubling the item templates.
     *
     * Found by reading the generated spec for the new clause rather than by a
     * test going red, which is the direction this repository keeps recording:
     * a change that makes an adjacent mechanism vacuous breaks nothing that
     * anybody is asserting.
     */
    items: itemBranches(req.shape, new Set(carded), {
      kind: 'card', id: 'item', bindings,
      ...(children.length || proseChildren.length
        ? { children: [...children, ...proseChildren] } : {}),
    } as TNode),
  };
}

/**
 * A role that SPLITS this pool: some objects have it, most-or-none do not.
 *
 * The band matters. At coverage near 1 every object has it, so it distinguishes
 * nothing; near 0 it is a stray property on one record rather than a kind of
 * thing. Between them is a pool that genuinely holds two kinds — the archive
 * pool is the case: two films with video and a poster, two recordings with
 * audio and neither.
 *
 * Restricted to media and av roles on purpose. A missing `author` is a gap in a
 * record; a missing `media.video` is a different KIND of object, and only the
 * second is worth a different card. Widening this to every role would branch a
 * pool on whatever happened to be half-filled.
 */
function splittingRole(shape: ObjectShape, drawn: ReadonlySet<Role>): Role | undefined {
  return shape.roles
    .filter((r) => (ROLE_KIND[r.role] === 'media' || ROLE_KIND[r.role] === 'av')
      && r.coverage >= 0.2 && r.coverage <= 0.8)
    // Drawn, or the two branches differ by a role neither of them renders.
    .filter((r) => drawn.has(r.role))
    .sort((a, b) => Math.abs(0.5 - a.coverage) - Math.abs(0.5 - b.coverage))[0]?.role;
}

/**
 * One item template, or two guarded ones.
 *
 * `ACCEPTANCE.md` closes with the line that outranks the four rows above it:
 * *"a form none of them can express"*. This is that mechanism. A Notion
 * database has ONE schema, so an object missing a property shows an empty cell;
 * a guarded collection gives it a different card. Obsidian has no schema to
 * miss, Coda's tables are built by hand, and Fabric curates.
 *
 * The schema has allowed eight guarded branches since the beginning and the
 * generator emitted exactly one, every time — so the renderer's `[0]` was never
 * wrong in practice and the whole mechanism sat unreachable. It is reachable
 * now, and derived from shape rather than authored: nobody writes the second
 * card, the pool having two kinds of thing in it is what produces one.
 */
/** Every `id` in a subtree, suffixed.
 *
 *  Ids are unique across the WHOLE spec, not per branch — the validator says
 *  `duplicate-id ... already used at /root/items/0/...` — so two branches
 *  cloned from one card collide on every child they share. Measured as three
 *  refusals on the first ragged pool this touched. */
function reid(node: TNode, suffix: string): TNode {
  const n = node as TNode & { id?: string; children?: TNode[] };
  return {
    ...n,
    ...(n.id ? { id: `${n.id}${suffix}` } : {}),
    ...(n.children ? { children: n.children.map((c) => reid(c, suffix)) } : {}),
  } as TNode;
}

/** A subtree with every binding on `role` removed, from `bindings` and from
 *  `children` alike. The default branch is for objects that do not have the
 *  role, and a node bound to a property they lack is the empty cell this whole
 *  mechanism exists to avoid. */
function stripRole(node: TNode, role: Role): TNode {
  const n = node as TNode & {
    bindings?: TBinding[]; binding?: TBinding; children?: TNode[];
  };
  return {
    ...n,
    ...(n.bindings ? { bindings: n.bindings.filter((b) => b.role !== role) } : {}),
    ...(n.children
      ? { children: n.children
          .filter((c) => (c as { binding?: TBinding }).binding?.role !== role)
          .map((c) => stripRole(c, role)) }
      : {}),
  } as TNode;
}

function itemBranches(
  shape: ObjectShape, drawn: ReadonlySet<Role>, base: TNode,
): { when?: { has: Role }; spec: TNode }[] {
  const split = splittingRole(shape, drawn);
  if (!split) return [{ spec: base }];
  // A DEEP COPY per branch. Both branches derive from one card, and spreading
  // it twice leaves them sharing the identical `children` array and the
  // identical child objects — which the spec gate reports as
  // `cyclic — value contains a cycle`, because a walker that remembers what it
  // has seen cannot tell a shared subtree from a loop. Measured: every
  // generated spec for a ragged pool was refused, and the message named a
  // cycle that was not there.
  const card = structuredClone(base) as TNode;
  const other = structuredClone(base) as TNode;
  // The branch FOR the role, and the branch without it. The second is the
  // default — the validator requires a `when` on every item but the last — and
  // it drops the binding rather than keeping it, because a card bound to a
  // property its objects do not have is the empty cell this exists to avoid.
  // The GUARDED branch is the base card unchanged. It already renders the
  // split role — that is what made the pool ragged in the first place — so
  // adding a binding for it drew the video TWICE on every film, which
  // `e2e/smoke.mjs` caught as two players sharing one accessible name.
  const withRole: TNode = { ...reid(card, '-a'), id: 'item-with' } as TNode;
  // The DEFAULT branch is that card with the split role taken out, from
  // bindings AND children — the media lives in `children`, so filtering only
  // `bindings` removed nothing at all.
  const without: TNode = { ...stripRole(reid(other, '-b'), split), id: 'item-without' } as TNode;
  return [{ when: { has: split }, spec: withRole }, { spec: without }];
}

/**
 * WHICH relation this lens gives a region of its own, if any.
 *
 * Its own function because `buildSingle` has to know the answer twice: once to
 * emit the region, and once to keep the same role out of `fields`. Deriving it
 * in two places is how the reference came to be drawn twice.
 *
 * Both thresholds decide only HERE — nothing in `buildCollection` reads either
 * of them. That matters, because the reason written beside
 * `relationSurfaceThreshold` is about cards: *"a reference to a peer in the
 * same collection points at something already on screen, so drawing it on every
 * card is repetition."* True as a statement about collections, and never what
 * this line prevented; a single-object lens has no second card to repeat onto.
 * The rule is recorded where it is read rather than where it sounded right.
 *
 * What the threshold does decide is real: whether a relation this pool mostly
 * does not have is worth a region. `relationDensity` is a share of objects now
 * — see `shape.ts` — so `0.2` means one object in five, which is what a reader
 * would have assumed it meant all along.
 */
function surfacedRelations(
  req: GenerationRequest, params: ResolvedParams, ranked: readonly Role[],
): readonly Role[] {
  // DECLARATION first, and it is not a threshold. See `declaresRelations` on
  // `ObjectShape`: attach lives on a node slot and nowhere else, so a lens that
  // emits no slot offers no gesture — and a pool under the threshold then has
  // no way to create the reference that would raise its density. A pool nobody
  // has bootstrapped would be permanently unwritable. Found by @Codex.
  //
  // The thresholds still decide everything they were for: whether a relation
  // the pool does not DECLARE, but whose objects happen to carry one, deserves
  // a region. That is the case a shape derived from a bag of objects is in.
  const declared = req.shape.declaresRelations === true;
  const surface = declared
    || req.shape.relationDensity > params.relationSurfaceThreshold
    || (req.shape.crossDocDensity ?? 0) > params.crossDocSurfaceThreshold;
  // `isRelationRole`, not `ROLE_KIND === 'ref'`. Four roles are ref-kind and
  // exactly one of them — `source` — is not relation-mutable: it means where a
  // value came from, not a link a person may add or remove. `mutationLegalFor`
  // refuses `link` on it and the demo's `withRelationMutations` leaves its slot
  // without mutations, so selecting it hands the lens's one slot to the one ref
  // role no gesture can write, and the pool is exactly as unbootstrappable as
  // it was before this function existed.
  //
  // Kind is about how a value is SHAPED; this decision is about what may be
  // DONE to it, and those are different questions that happen to agree on three
  // roles out of four. Found by @Codex reviewing #119 after it merged — and the
  // same predicate had already been corrected from kind to `isRelationRole` in
  // the demo host during that review, one layer out, without being carried back
  // here.
  if (!surface) return [];
  // A declaration is a capability contract: every declared mutable relation
  // needs a bootstrap gesture. An inferred shape is only a density-based
  // presentation choice, so it remains capped at one row.
  //
  // The declared branch reads the SHAPE, not `ranked` — and that is the whole
  // of @Codex's finding. `rankRoles` drops every role under
  // `roleCoverageFloor`, which is a PRESENTATION rule: it keeps a lens from
  // being cluttered by a property two objects out of a hundred carry. Applied
  // to a declared relation it becomes a REACHABILITY rule instead, and a
  // relation on 10% of a pool gets no slot, no Add, and therefore no way to
  // become an 11th — which is exactly the sparse-pool bootstrap failure this
  // whole feature exists to fix, surviving in a new place.
  //
  // A shape that DECLARES a relation is asserting the relation applies to every
  // object of the type, whatever share of them happen to carry one today. That
  // is the same argument `declaresRelations` was added for; the coverage floor
  // was simply one layer further out than anyone looked.
  //
  // Every relation role carries the same rank weight, so ordering them by
  // coverage reproduces `rankRoles`' order for relations exactly — the floor is
  // the only thing being skipped, not the ranking.
  if (declared) {
    return req.shape.roles
      .filter((r) => isRelationRole(r.role))
      // SUPPRESSION still applies. `ranked` is filtered by two different rules
      // and reading past it skipped both — the coverage floor, which is what
      // this branch is for, and `taste.suppressedRoles`, which it has no
      // business touching. @Codex: *"the resulting lens therefore emits that
      // role's `node-slot` and Add/Remove controls despite the explicit taste
      // signal, contradicting the existing `taste beats inference` contract."*
      //
      // The distinction is the whole point of the branch and I lost it by
      // reaching for the raw shape: a coverage floor is the generator's OWN
      // guess about what is worth drawing, and a declaration outranks a guess.
      // Suppression is the person saying they do not want to see this, and
      // nothing outranks that — least of all a pool's declaration, which is
      // what they were suppressing.
      .filter((r) => !req.taste?.suppressedRoles?.includes(r.role))
      .slice()
      .sort((a, b) => b.coverage - a.coverage)
      .map((r) => r.role);
  }
  return ranked.filter((r) => isRelationRole(r)).slice(0, 1);
}

function buildSingle(
  req: GenerationRequest, ranked: Role[], params: ResolvedParams,
): TNode {
  const hero = pickHero(req.shape, params, new Set(ranked));
  const choose = chooseableEnums(req.shape, params, req.enumOptionCount);
  const surfaced = surfacedRelations(req, params, ranked);
  // A surfaced relation KEEPS its field, and that is deliberate rather than an
  // oversight. The two are different affordances on one role: the field is a
  // chip you hover to peek at the target without leaving, and the region is the
  // list you attach to and detach from. Notion ships both for the same reason.
  //
  // A first version of this work removed the field, on the reasoning that one
  // role belongs in one place — and it took the demo's ONLY hover preview with
  // it. Measured: `.m-ref-hit` went to zero in every pool and every lens, so
  // `e2e/smoke.mjs` lost the assertion behind the Obsidian row's *"hover
  // preview"* item. The duplication is also what dense pools have always
  // shipped, since they cross the threshold; it is a question worth asking
  // separately, and it is not this cycle's to answer by breaking a row.
  const fields = ranked.filter((r) => r !== hero && !isVisual(r));
  // Recordings get their own nodes rather than a text field. A single-object
  // lens is where a trailer actually belongs — a grid tile has no room to play
  // anything, which is why this is here and not in `buildCollection`.
  // Recordings AND documents. Both carry a blob the runtime resolves and both
  // need a `media` node to reach that path; the comment two lines up promised
  // the next kind would not have to relearn this, so `doc` is here rather than
  // in a second list that could fall out of step with the first.
  const attached = ranked.filter((r) => ROLE_KIND[r] === 'av' || ROLE_KIND[r] === 'doc');
  // A trailer, THEN its transcript — and the first version of this said exactly
  // that in a comment while appending every recording after every text field,
  // so a transcript printed above the thing it transcribes. A comment that
  // describes the intent rather than the code is the failure this repo polices
  // hardest, and I wrote one. Found by @Codex.
  //
  // Emitted as `media` nodes because that is what the runtime renders
  // time-based media with: the ROLE carries the distinction and the renderer
  // branches on `ROLE_KIND`, rather than adding a node kind nobody needed.
  const bodyChildren: TNode[] = [
    ...attached.map((r, i) => ({
      kind: 'media' as const,
      id: `av-${i}`,
      binding: bindingFor(`av-${i}`, r, choose),
      fit: 'contain' as const,
    })),
    ...fields.map((r, i) => ({
      kind: 'field' as const,
      id: `f-${i}`,
      binding: bindingFor(`s-${i}`, r, choose),
    })),
  ];

  if (surfaced.length) {
    bodyChildren.push({
      // `purpose`, not the id. The renderer reads this; a region called
      // "relations" that happened to hold embeds is not one, and a relation
      // stack built under another id still is.
      kind: 'region', id: 'relations', purpose: 'relations', flow: 'flow',
      // The id derives from the ROLE, never from the position.
      //
      // `relation-${i}` was stable only while nothing moved, and a relation
      // region is a list that moves by construction: removing an object's last
      // Background reference drops `relation.peer` down the ranking, so
      // `relation-0` stopped meaning peer and started meaning parent — the same
      // name for a different thing. The store's authority maps spec id to role,
      // so the visible row and the slot that would be written diverged.
      //
      // Found by @Codex driving the deployed two-relation Task: attach both,
      // remove Background, then Remove on Parent task is refused with *"this
      // control does not write relation.peer"*. That refusal was the #120
      // slot-binding guard doing its job — it turned a write through the wrong
      // slot into a visible refusal rather than a silent mis-link — but the
      // divergence underneath it was this.
      children: surfaced.map((via) => ({
        kind: 'node-slot' as const, id: `rel-${via.replace(/\./g, '-')}`, via, maxDepth: 1,
      })),
    });
  }

  /* THE EDITOR DECLARATION — decision `0076`, @Codex, 2026-09-06.
     `purpose` is what makes this region the page's primary editorial surface,
     and the renderer promotes a single-object region's title to the page
     heading only when it is DECLARED. Before this, the ordinary object page's
     only heading was `H2: Connections` — the relations fold — so a person
     pressing H was told the page is about its own footer.

     The alternative was relaxing the renderer to `cardinality === 'single' &&
     parent.kind === 'region'`, and `0076` refuses it: that promotes the title
     of ANY region under a single-object spec, including a related item, a
     preview or an embedded editor. That is their reasoning and not a
     measurement of mine: the relations region below declares its own purpose
     and so is not promoted either way, and no fixture in this repository
     carries a nested region that WOULD be — which is why 0076's acceptance
     clause 5 has no clause behind it and the question is back with them.

     It is NOT gated on a write grant. `editor` names the region's semantic
     function, not the viewer's permission: a reader gets the same surface
     without the write affordances, so the heading hierarchy is the same for
     both. Gating this on MAY_WRITE would be the likeliest wrong
     implementation of the ruling. */
  const body: TNode = { kind: 'region', id: 'body', purpose: 'editor', flow: 'stack', children: bodyChildren };

  return hero
    ? {
        kind: 'region', id: 'sheet', flow: 'row',
        children: [
          { kind: 'media', id: 'hero',
            binding: { slot: 'hero', role: hero, direction: 'read' },
            fit: 'cover', aspect: '2/3' },
          body,
        ],
      }
    : body;
}

/** The generator is parameterized. `makeBaselineGenerator(overrides)` is what
 *  makes a cohort experiment possible — without it the registry is decorative,
 *  which is exactly what @Codex caught on PR #23. */
export function makeBaselineGenerator(overrides: ParamOverrides = {}): Generator {
  const params = resolveParams(overrides);
  return {
  name: `baseline@0.1.0${Object.keys(overrides).length ? '+tuned' : ''}`,

  propose(req: GenerationRequest): TLayoutSpec {
    /**
     * A REVISION when there is something to revise, decision `0045`.
     *
     * Before this line the field was read by nothing and every caller — the
     * stage repaint, `openOffer`, `openPoolOffer` — got a fresh baseline tree
     * while the type's own comment promised *"this is what keeps churn low"*.
     * `0121` records the whole of it, including why repairing the one path
     * @Codex found it in would have been worse than leaving it uniform.
     *
     * It is FIRST, before any of the fresh-design work below, because the
     * decision's contract is about what the person keeps rather than about what
     * this generator would otherwise have chosen: *"absence of an instruction
     * means preservation; it does not mean return to baseline."*
     *
     * A shape that has outgrown the arrangement throws `AcceptedNoLongerLegal`
     * rather than falling through to the code below — the fresh baseline is
     * exactly the loss `0045` refuses to make on a caller's behalf.
     */
    if (req.accepted) return reviseAccepted({ ...req, accepted: req.accepted });

    const ranked = rankRoles(req.shape, params, req.taste?.prominentRoles).filter(
      (r) => !req.taste?.suppressedRoles?.includes(r),
    );
    /**
     * A NAMED APP LOOK is a hard constraint on the whole tree — decision
     * 0055 — so it takes the ranking (taste's say over which properties
     * show, and in what order) and nothing below: the fresh-design work
     * under this line chooses topology, and the template has already chosen
     * it. Refuses in words when the host cannot draw what the template
     * requires; `compile` reports that refusal rather than falling back.
     */
    if (req.template) {
      const choose = chooseableEnums(req.shape, params, req.enumOptionCount);
      return buildAppTemplate(
        { ...req, template: req.template }, ranked,
        (slot, role) => bindingFor(slot, role, choose));
    }
    const appetite = appetiteFor(req, params);

    // THE CONTROL AND ITS TWO REGIONS, or neither. @Codex's ruling on 0079: a
    // slot alone "is an unexplained control", and a predicate in one region is
    // an ordinary filter box.
    //
    // Both regions are the SAME collection over the same source, differing only
    // by the predicate — region A above the threshold, region B `not` of the
    // identical predicate. A legible partition rather than two arbitrary lists,
    // and deliberately the partition already proved at the store layer in
    // `view-state-across-regions.test.ts`.
    // COMPARING ONLY, and this is a deviation from the ruling that a
    // measurement forced.
    //
    // @Codex ruled that a slot is earned whenever the shape warrants one. Built
    // that way and driven on the page, the control WORKS — one date split the
    // films pool into 7 above and 5 at-or-below, from a single input, which is
    // Coda's mechanism running for the first time. And the default view became
    // THE POOL TWICE: 36 cards for 12 films, because an unset slot leaves both
    // predicates inactive and `not(absent)` is absent, so region A shows
    // everything and region B shows everything.
    //
    // A lens that shows every object twice until you touch a control is not a
    // lens anybody keeps, and "the first paint remains unset and therefore
    // unfiltered" did not anticipate that unfiltered TWICE is the cost. So the
    // split is offered where a person has said they are comparing, and every
    // other context gets the single collection it had. Raised with @Codex with
    // the numbers rather than settled here.
    const slot = req.shape.cardinality === 'single' || !params.splitOnViewState
      ? undefined
      : pickViewStateSlot(req.shape, params, ranked);

    const collection = req.shape.cardinality === 'single'
      ? undefined
      : buildCollection(req, ranked, params, appetite);

    // A FUNCTION, not a value. Region B wraps the identical predicate in `not`,
    // and handing both regions ONE object leaves the same node reachable twice
    // — which `preScan` reports as `cyclic — value contains a cycle`, refusing
    // the whole lens. `itemBranches` already carries this scar for its two card
    // branches; this is the same one, in the predicate.
    const where = slot
      ? () => ({ where: { role: slot.role, op: slot.op, valueFrom: 0 } })
      : undefined;
    const root =
      req.shape.cardinality === 'single'
        ? buildSingle(req, ranked, params)
        : slot && where
          ? {
            kind: 'region' as const,
            id: 'split',
            flow: 'stack' as const,
            children: [
              partitioned(collection!, BRANCH_A, where()),
              partitioned(collection!, BRANCH_B, { not: where() }),
            ],
          } as unknown as TNode
          : collection!;

    return {
      specVersion: SPEC_VERSION,
      vocabularyVersion: VOCABULARY_VERSION,
      /**
       * THE PARTITION, emitted with the regions it governs.
       *
       * Without this the split is undeclared, and an undeclared split is the
       * defect @Codex ruled on in decision 0020: an unset slot leaves both
       * predicates inert, so both regions are unfiltered and the lens paints
       * everything twice. The host is forbidden from working that out — *"the
       * host may execute that declared unset policy, but must not derive it by
       * comparing queries or interpreting complementarity"* — so if the
       * generator does not say it, nobody may.
       *
       * `unset.show` is the `where` branch. Both branches are unfiltered while
       * the slot is empty, so either would show the whole set; naming the
       * positive one means the lens a person first sees is the collection they
       * would have had before the split existed.
       *
       * The ids are `partitioned`'s own — it renames the whole subtree
       * `${id}-${branch}` and the collection to `collection-${branch}`, because
       * `validate.ts` refuses `duplicate-id` across a spec and both branches
       * are copies of one tree. Built from the same constants the children are,
       * so a rename cannot leave the declaration pointing at nothing: that is
       * `partition-branch-is-not-a-node`, and it would refuse the whole lens.
       */
      ...(slot
        ? {
          partition: {
            valueFrom: 0,
            branches: [`collection-${BRANCH_A}`, `collection-${BRANCH_B}`],
            unset: { show: `collection-${BRANCH_A}` },
          },
        }
        : {}),
      ...(slot
        ? {
          viewState: [{
            // The FALLBACK caption, not the one a person usually reads. The
            // schema requires a name and the spec may carry nothing about the
            // pool, so this is derived from the vocabulary term; the renderer
            // prefers the host's declared label for the same role and falls
            // back to this. See `nameOfRole` above for what that cost before.
            name: nameOfRole(slot.role),
            role: slot.role,
            op: slot.op,
          }],
        }
        : {}),
      target: {
        cardinality: req.shape.cardinality,
        mediaWeight: req.shape.hasMedia
          ? req.shape.roles.filter((r) => ROLE_KIND[r.role] !== 'media').length > 3
            ? 'mixed'
            : 'media'
          : 'text',
        context: req.context,
      },
      // Explicit taste beats inference, always. A stated preference that keeps
      // getting overridden is the fastest way to lose someone's trust.
      density: req.taste?.density ?? nearestDensity(appetite),
      typography: req.taste?.typography ?? pickTypography(req.shape, req.context, appetite),
      palette: req.taste?.palette ?? 'neutral',
      root,
    };
  },
  };
}

/** The default instance, on shipped priors. */
export const baselineGenerator: Generator = makeBaselineGenerator();
